import { t as ErrorMessage } from "./ErrorMessage-BqOEJm84.mjs";
import React from "react";
import { jsx, jsxs } from "react/jsx-runtime";
import { Controller } from "react-hook-form";
import CodeEditor from "@uiw/react-textarea-code-editor";
import CodeEditorNoHighlight from "@uiw/react-textarea-code-editor/nohighlight";

//#region src/components/fields/CodeEditorField.tsx
/**
* CodeEditorField provides syntax-highlighted code editing with form integration.
*
* Features:
* - Syntax highlighting via @uiw/react-textarea-code-editor
* - React Hook Form integration with Controller
* - Configurable language support (JSON, TypeScript, etc.)
* - Performance optimizations with smart highlighting
* - Constrained height with automatic scrolling
* - Design system styling integration
*
* @example
* ```tsx
* <CodeEditorField
*   id="contractAbi"
*   name="contractSchema"
*   control={control}
*   label="Contract ABI"
*   language="json"
*   placeholder="Paste your ABI JSON here..."
* />
* ```
*/
function CodeEditorField({ id, name, control, label, helperText, placeholder = "", language = "json", theme = "light", height = "200px", maxHeight = "400px", performanceThreshold = 5e3, required = false, disabled = false, readOnly = false, validateCode, className }) {
	function extractPixelValue(val, fallback) {
		if (typeof val === "number") return val;
		const match = typeof val === "string" ? val.match(/^(\d+)\s*px$/) : null;
		if (match) return parseInt(match[1], 10);
		return fallback;
	}
	const minHeightNum = extractPixelValue(height, 200);
	const maxHeightNum = extractPixelValue(maxHeight, 400);
	return /* @__PURE__ */ jsxs("div", {
		className,
		children: [label && /* @__PURE__ */ jsxs("label", {
			htmlFor: id,
			className: "block text-sm font-medium text-foreground mb-2",
			children: [label, required && /* @__PURE__ */ jsx("span", {
				className: "text-destructive ml-1",
				children: "*"
			})]
		}), /* @__PURE__ */ jsx(Controller, {
			name,
			control,
			rules: {
				required: required ? "This field is required" : false,
				validate: { validCode: (value) => {
					if (!validateCode || !value) return true;
					const validation = validateCode(value);
					if (typeof validation === "string") return validation;
					if (validation === false) return "Invalid code format";
					return true;
				} }
			},
			render: ({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
				const shouldDisableHighlighting = (value || "").length > performanceThreshold;
				const EditorComponent = shouldDisableHighlighting ? CodeEditorNoHighlight : CodeEditor;
				const handleChange = (event) => {
					onChange(event.target.value);
				};
				const handleBlur = () => {
					onBlur();
				};
				return /* @__PURE__ */ jsxs("div", {
					className: "space-y-2",
					children: [
						/* @__PURE__ */ jsx("div", {
							className: "w-full rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 disabled:cursor-not-allowed resize-y",
							style: {
								maxHeight: `${maxHeightNum}px`,
								overflow: "auto",
								overflowX: "hidden",
								minHeight: `${minHeightNum}px`,
								resize: "vertical"
							},
							children: /* @__PURE__ */ jsx(EditorComponent, {
								id,
								value: value || "",
								language,
								placeholder,
								onChange: handleChange,
								onBlur: handleBlur,
								padding: 12,
								minHeight: minHeightNum,
								"data-color-mode": theme,
								disabled,
								readOnly,
								"data-testid": `${id}-code-editor${shouldDisableHighlighting ? "-no-highlight" : ""}`,
								className: "text-sm placeholder:text-muted-foreground",
								style: {
									fontFamily: "ui-monospace, SFMono-Regular, \"SF Mono\", Consolas, \"Liberation Mono\", Menlo, monospace",
									fontSize: "14px",
									border: "none",
									backgroundColor: "transparent",
									width: "100%",
									wordWrap: "break-word",
									whiteSpace: "pre-wrap",
									overflowWrap: "break-word"
								}
							})
						}),
						/* @__PURE__ */ jsx(ErrorMessage, {
							error,
							id: `${id}-error`
						}),
						helperText && !error && /* @__PURE__ */ jsx("p", {
							className: "text-sm text-muted-foreground",
							id: `${id}-helper`,
							children: helperText
						})
					]
				});
			}
		})]
	});
}
CodeEditorField.displayName = "CodeEditorField";

//#endregion
export { CodeEditorField };
//# sourceMappingURL=code-editor.mjs.map